import styled from '@emotion/styled';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import LoadingButton from '@mui/lab/LoadingButton';
import {
  Box,
  FormControl,
  IconButton,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import {
  ILoginFormInput,
  useLoginForm,
} from 'components/providers/LoginFormProvider';
import { useEffect, useState } from 'react';
import { Controller, SubmitHandler } from 'react-hook-form';
import { postLogin } from 'utils/fetch';
import ja from 'utils/ja';

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
  margin-top: '15px';
  margin-bottom: '15px';
`;

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface ILoginFormProps {}

const LoginForm: React.FC<ILoginFormProps> = () => {
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const { control, handleSubmit, setError } = useLoginForm();
  const theme = useTheme();
  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  const { isError, error, isPending, mutate } = useMutation({
    mutationFn: postLogin,
  });

  const onSubmit: SubmitHandler<ILoginFormInput> = (data) => {
    mutate({ body: data });
  };

  useEffect(() => {
    if (isError) {
      const errorMessage =
        ((error as AxiosError).response?.data as { response_message: string })
          .response_message || 'ログインIDまたはパスワードが正しくありません。';
      setError('username', {
        type: 'server',
        message: errorMessage,
      });
      setError('password', {
        type: 'server',
        message: errorMessage,
      });
    }
  }, [isError]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        height: '100%',
      }}
    >
      <Box
        sx={{
          width: '100%',
          flex: 1,
          marginY: '40px',
        }}
      >
        <StyledForm onSubmit={handleSubmit(onSubmit)}>
          <Controller
            name='username'
            control={control}
            render={({ field, fieldState: { error } }) => (
              <FormControl variant='standard'>
                <TextField
                  fullWidth
                  id='username'
                  label={ja.login.username.label}
                  {...field}
                  onBlur={(event) => {
                    field.onChange(event.target.value);
                    field.onBlur();
                  }}
                />
                {error?.message && (
                  <Typography
                    sx={{
                      color: theme.palette.error.main,
                      fontSize: theme.typography.fontSize,
                      my: '5px',
                    }}
                  >
                    {error?.message}
                  </Typography>
                )}
              </FormControl>
            )}
          />
          <Controller
            name='password'
            control={control}
            render={({ field, fieldState: { error } }) => (
              <FormControl variant='standard'>
                {/* <InputLabel required shrink htmlFor='password'>
                  {ja.login.password.label}
                </InputLabel> */}
                <TextField
                  //placeholder={ja.login.password.placeholder}
                  id='password'
                  label={ja.login.password.label}
                  type={showPassword ? 'text' : 'password'}
                  fullWidth
                  InputProps={{
                    endAdornment: (
                      <IconButton
                        aria-label='toggle password visibility'
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                        edge='end'
                        sx={{
                          position: 'absolute',
                          right: '15px',
                        }}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    ),
                  }}
                  {...field}
                  onBlur={(event) => {
                    field.onChange(event.target.value);
                    field.onBlur();
                  }}
                  sx={{ mt: 2 }}
                />
                {error?.message && (
                  <Typography
                    sx={{
                      color: theme.palette.error.main,
                      fontSize: theme.typography.fontSize,
                      my: '5px',
                    }}
                  >
                    {error?.message}
                  </Typography>
                )}
              </FormControl>
            )}
          />
          <LoadingButton
            loading={isPending}
            variant='contained'
            type='submit'
            color='secondary'
            fullWidth
            sx={{
              height: '56px',
              fontSize: '22px',
              mt: 2,
            }}
          >
            {ja.login.submit}
          </LoadingButton>
        </StyledForm>
      </Box>
    </Box>
  );
};

export default LoginForm;
